import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Clock, BookOpen, Trophy, Award, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import type { Course } from '../types/course';

export function CourseMarketplace() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLevel, setSelectedLevel] = useState<string[]>([]);
  const [selectedDuration, setSelectedDuration] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        loadCourses(),
        loadCategories()
      ]);
    };
    init();
  }, []);

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('course_categories')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*, course_marketplace_listings(price, sale_price, sale_ends_at)')
        .eq('is_published', true)
        .is('deleted', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLevel = selectedLevel.length === 0 || selectedLevel.includes(course.level);
    const matchesDuration = selectedDuration.length === 0 || selectedDuration.includes(course.duration);
    const matchesCategory = selectedCategory.length === 0 || selectedCategory.includes(course.category_id);
    
    return matchesSearch && matchesLevel && matchesDuration && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-accent-green hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Dashboard
          </Link>
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-black">Course Marketplace</h1>
          <p className="text-black mt-2">Discover courses to advance your skills and career</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-3">
            <div className="bg-background-card rounded-xl p-6 border border-gray-800">
              <div className="flex items-center space-x-2 mb-6">
                <Filter className="h-4 w-4 text-gray-400" />
                <h2 className="text-sm font-medium text-black">Filters</h2>
              </div>

              <div className="space-y-6">
                {/* Category Filter */}
                <div>
                  <h3 className="text-[#800020] text-sm font-medium mb-3">Category</h3>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <label key={category.id} className="flex items-center group">
                        <input
                          type="checkbox"
                          checked={selectedCategory.includes(category.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCategory([...selectedCategory, category.id]);
                            } else {
                              setSelectedCategory(selectedCategory.filter(id => id !== category.id));
                            }
                          }}
                          className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
                        />
                        <span className="ml-2 text-xs text-black">
                          {category.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Level Filter */}
                <div className="mb-6">
                  <h3 className="text-[#800020] text-sm font-medium mb-3">Level</h3>
                  <div className="space-y-2">
                    {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                      <label key={level} className="flex items-center group">
                        <input
                          type="checkbox"
                          checked={selectedLevel.includes(level)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLevel([...selectedLevel, level]);
                            } else {
                              setSelectedLevel(selectedLevel.filter(l => l !== level));
                            }
                          }}
                          className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
                        />
                        <span className="ml-2 text-xs text-black">
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Duration Filter */}
                <div>
                  <h3 className="text-[#800020] text-sm font-medium mb-3">Duration</h3>
                  <div className="space-y-2">
                    {['0-20 hours', '20-40 hours', '40+ hours'].map((duration) => (
                      <label key={duration} className="flex items-center group">
                        <input
                          type="checkbox"
                          checked={selectedDuration.includes(duration)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDuration([...selectedDuration, duration]);
                            } else {
                              setSelectedDuration(selectedDuration.filter(d => d !== duration));
                            }
                          }}
                          className="rounded border-gray-700 text-accent-green focus:ring-accent-green bg-background-dark"
                        />
                        <span className="ml-2 text-xs text-black">
                          {duration}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-background-card border border-gray-800 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
                />
              </div>
            </div>

            {/* Course Grid */}
            {isLoading ? (
              <div className="flex justify-center p-8">
                <LoadingSpinner />
              </div>
            ) : filteredCourses.length === 0 ? (
              <div className="text-center py-12 text-black">
                <p>No courses found matching your criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.map((course) => (
                  <div
                    key={course.id}
                    className="bg-background-card rounded-xl overflow-hidden shadow-lg transition-all"
                  >
                    <div className="relative h-48">
                      <img
                        src={course.image_url}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    
                    <div className="p-6">
                      <h2 className="text-xl font-semibold text-black mb-2">{course.title}</h2>
                      <p className="text-black text-sm mb-4">{course.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-black text-sm">
                          <BookOpen className="h-4 w-4 mr-2" />
                          {course.provider}
                        </div>
                        <div className="flex items-center text-black text-sm">
                          <Clock className="h-4 w-4 mr-2" />
                          {course.duration}
                        </div>
                        <div className="flex items-center text-black text-sm">
                          <Trophy className="h-4 w-4 mr-2" />
                          {course.level}
                        </div>
                        {course.salary_range && (
                          <div className="flex items-center text-black text-sm">
                            <DollarSign className="h-4 w-4 mr-2" />
                            {course.salary_range}
                          </div>
                        )}
                        {course.has_certification && (
                          <div className="flex items-center text-emerald-400 text-sm">
                            <Award className="h-4 w-4 mr-2" />
                            Certification Available
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        {course.course_marketplace_listings?.[0] && (
                          <div>
                            {course.course_marketplace_listings[0].sale_price ? (
                              <div className="flex items-center space-x-2">
                                <span className="text-2xl font-bold text-[#d8f477]">
                                  ${(course.course_marketplace_listings[0].sale_price / 100).toFixed(2)}
                                </span>
                                <span className="text-sm text-black line-through">
                                  ${(course.course_marketplace_listings[0].price / 100).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-2xl font-bold text-[#d8f477]">
                                {course.course_marketplace_listings[0].price === 0 ? 'Free' : `$${(course.course_marketplace_listings[0].price / 100).toFixed(2)}`}
                              </span>
                            )}
                          </div>
                        )}
                        <Link
                          to={course.affiliate_url || `/courses/${course.id}`}
                          target={course.affiliate_url ? "_blank" : undefined}
                          rel={course.affiliate_url ? "noopener noreferrer" : undefined}
                          className="px-4 py-2 bg-black text-white rounded-lg hover:opacity-90 transition-colors"
                        >
                          {course.affiliate_url ? 'Enroll Now' : 'View Course'}
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}